<script setup lang="ts">
import { onMounted, Ref, ref, watch, computed, onBeforeUnmount } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import {
  useAuthStore,
  useCalendarStore,
  useEventsModalsStore,
  useOrganizationStore,
  useUtilizationStore,
} from '@/store';
import listPlugin from '@fullcalendar/list';
import AutoComplete from 'primevue/autocomplete';
import Calendar from 'primevue/calendar';
import {
  can,
  useGetInitialDropdownOptions,
  usePersonTypes,
  modifiedRRulePlugin,
  useDateTime,
  useRequestCancel,
  useTenant,
} from '@/composables';
import { AttendeeDropdownOptionInterface, FiltersTherapyEventsInterface } from '@/types';
import SupportWidget from '@/components/SupportWidget/SupportWidget.vue';
import Evaluation from '@/components/Evaluation/Evaluation.vue';
import DashboardCaseloadAdditions from './components/DashboardCaseloadAdditions.vue';
import Attendance from '@/components/Attendance/Attendance.vue';
import SOWReporting from '@/components/Utilization/SOWReporting.vue';
import TimeRatioReporting from '@/components/Utilization/TimeRatioReporting.vue';
import EventsModals from '@/components/EventsModals';
import { PERMISSIONS, EVENT_TYPE, TENANT_SETTINGS } from '@/enum';
import Provider from '@/components/Provider';
import { Overage } from '..';

const $tenant = useTenant();
const authStore = useAuthStore();
const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const organizationStore = useOrganizationStore();
const utilizationStore = useUtilizationStore();
const requestCancel = useRequestCancel();
const today = new Date();
const calendar = ref();
const isClinician = ref(usePersonTypes.isClinician() || usePersonTypes.isCaseManager());
const isCaseManager = usePersonTypes.isCaseManager();
const isSupportAdminOrSchoolAdmin = usePersonTypes.isSupportAdminOrSchoolAdmin();
const isSuperAdmin = usePersonTypes.isSuperAdmin();
const canViewUtilizationWidgets =
  can([PERMISSIONS.CLINICIAN_ANALYTICS]) &&
  (isSuperAdmin || usePersonTypes.isSupportAdmin() || usePersonTypes.isBillingAdmin());
const selectedAttendees = ref<AttendeeDropdownOptionInterface[]>([]);
const firstDay = ref<string>(new Date(today.getFullYear(), today.getMonth(), 1).toLocaleDateString('fr-CA'));
const lastDay = ref<string>(new Date(today.getFullYear(), today.getMonth() + 1, 0).toLocaleDateString('fr-CA'));
const availableAttendees = ref([] as AttendeeDropdownOptionInterface[]);
availableAttendees.value =
  isClinician.value && can([PERMISSIONS.CLINICIAN_STUDENTS_INDEX])
    ? await useGetInitialDropdownOptions(authStore.user?.id as number).then((result) => {
        return result.availableAttendees.value;
      })
    : [];

const allAttendees = [...availableAttendees.value];

const filteredStudents = ref();

const autocomplete = ref(false);

const organizationAssociated = computed(() => authStore.organizationAssociated.map((organization) => organization.key));

const canAccessClassroom = computed(() => {
  return isClinician.value || isParentOrFacilitatorOrStudent.value || usePersonTypes.isSchoolAdmin();
});

const isParentOrFacilitatorOrStudent = computed(() => {
  return usePersonTypes.isFacilitator() || usePersonTypes.isParent() || usePersonTypes.isStudent();
});

onMounted(async () => {
  if ($tenant.tenantSettingEnabled(TENANT_SETTINGS.UTILIZATION_TRACKING) && canViewUtilizationWidgets) {
    await utilizationStore.getUtilizationDashboardWidgetData();
  }

  if (can([PERMISSIONS.THERAPY_EVENT_INDEX])) {
    const filtersEvents: FiltersTherapyEventsInterface = can([PERMISSIONS.THERAPY_EVENT_INDEX_ALL])
      ? {
          start_at: firstDay.value,
          end_at: lastDay.value,
        }
      : {
          organizations: organizationAssociated.value,
          start_at: firstDay.value,
          end_at: lastDay.value,
        };

    if (organizationStore.organizationsIdsSelected.length > 0) {
      await getEventsBySelectedOrganizations();
    } else {
      await calendarStore.getEvents(filtersEvents);
    }

    await updateSelectedDate();
    filterIsGroupOrIndividualAttendee();
  }
});

onBeforeUnmount(() => {
  requestCancel.requestSaverAbortAll();
});

const isGroupEligibleAttendees = ref<AttendeeDropdownOptionInterface[]>([]);

const filterSelected = () => {
  if (!selectedAttendees.value.length) {
    availableAttendees.value = allAttendees;
    autocomplete.value = false;
    return;
  }

  autocomplete.value = true;

  if (isGroupEligibleAttendees.value.includes(selectedAttendees.value[0])) {
    availableAttendees.value = isGroupEligibleAttendees.value;
    return;
  }

  availableAttendees.value = selectedAttendees.value;
};

const isAlreadySelected = (data: { value: number }) => {
  return !!selectedAttendees.value.find((st) => st.value === data.value);
};

const selectedDate: Ref<Date> = ref(today);

const eventsOnSelectedDate = ref(false);

watch([() => calendarStore.loading, () => selectedDate.value], () => {
  eventsOnSelectedDate.value = document.getElementsByClassName('fc-list-event').length > 0;
});

const updateSelectedDate = async () => {
  await calendar.value?.getApi().gotoDate(selectedDate.value);

  if (!isDayInActualMonth(selectedDate.value)) {
    firstDay.value = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth(), 1).toLocaleDateString(
      'fr-CA',
    );
    lastDay.value = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth() + 1, 0).toLocaleDateString(
      'fr-CA',
    );
    if (organizationStore.organizationsIdsSelected.length > 0) {
      await calendarStore.getEventsByOrganization(
        firstDay.value,
        lastDay.value,
        organizationStore.organizationsIdsSelected,
      );
    } else {
      await calendarStore.getEvents({ start_at: firstDay.value, end_at: lastDay.value });
    }
  }

  calendarOptions.value.events = calendarStore.events;
};

const getEventsBySelectedOrganizations = async () => {
  await calendarStore
    .getEventsByOrganization(firstDay.value, lastDay.value, organizationStore.organizationsIdsSelected)
    .then(() => {
      calendarOptions.value.events = calendarStore.events;
    });
};

watch(() => selectedDate.value, updateSelectedDate);

watch(
  () => eventsModalsStore.isAttendeesSelectedFromDashboard,
  () => {
    if (eventsModalsStore.isAttendeesSelectedFromDashboard) {
      selectedAttendees.value = [];
      autocomplete.value = false;
      availableAttendees.value = allAttendees;
    }
  },
);

watch(
  () => organizationStore.organizationsIdsSelected,
  async () => {
    await getEventsBySelectedOrganizations();
  },
);

watch(
  () => calendarStore.events,
  () => (calendarOptions.value.events = calendarStore.rawEvents),
);

watch(
  () => authStore.personType?.acronym,
  async () => {
    isClinician.value = usePersonTypes.isClinician() || usePersonTypes.isCaseManager();
  },
);

watch(
  () => authStore.impersonateToken,
  async (value, oldValue) => {
    if (oldValue) {
      await getEventsBySelectedOrganizations();
    }
  },
);

watch(
  () => calendarStore.rawEvents,
  () => {
    calendarOptions.value.events = calendarStore.rawEvents;
  },
);

const isDayInActualMonth = (date: Date) => {
  let strDate = date.toLocaleDateString('fr-CA');
  return strDate >= firstDay.value && strDate <= lastDay.value;
};

const calendarOptions = ref<CalendarOptions>({
  plugins: [listPlugin, modifiedRRulePlugin],
  initialView: 'listDay',
  headerToolbar: false,
  listDayFormat: { month: 'short', day: 'numeric', weekday: 'short' },
  stickyHeaderDates: false,
  listDaySideFormat: false,
  height: '100%',
  eventBackgroundColor: '#ffffff',
  eventBorderColor: '#f1f3f3',
  eventTextColor: '#3B4242',
  eventMinHeight: 24,
  events: [],
  eventClassNames: (arg) => calendarStore.defineEventsClassNames(arg),
  eventClick: function (info: EventClickArg) {
    const isDocumented = info.event._def.extendedProps.eventStatus === 'documented';
    const isInPast = (info.event.start as Date) < new Date();
    const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);
    const showDocumentEvent = isDocumented || isInPast;
    const isCaseloadAndInCurrentMonth =
      isCaseloadManagement && useDateTime().isSameMonthAndYear(info.event.start as Date);

    if (showDocumentEvent && !isCaseloadAndInCurrentMonth) {
      onDocumentEvent(info);
    } else {
      onEditEvent(info);
    }
  },
  eventDidMount: (info) => {
    const eventTimeTd = info.el.getElementsByClassName('fc-list-event-time')[0];
    const start = useDateTime().formatTimeByTimezone(
      info.event.start as Date,
      info.event.extendedProps.hostTimezoneName,
    );
    const end = useDateTime().formatTimeByTimezone(info.event.end as Date, info.event.extendedProps.hostTimezoneName);

    if (eventTimeTd) {
      //need to catch it on the next cycle because it still loads the orig text
      setTimeout(() => {
        eventTimeTd.innerHTML = `${start} - ${end}  ${info.event.extendedProps.hostTimezone}`;
      }, 0);
    }
  },
});

const onDocumentEvent = async (info: EventClickArg) => {
  eventsModalsStore.onDocumentEvent(info.event);
};

const onEditEvent = async (info: EventClickArg) => {
  const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);

  if (isSupportAdminOrSchoolAdmin || isSuperAdmin || isCaseManager) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  if (isCaseloadManagement) {
    eventsModalsStore.onEditEvent(info.event);
    return;
  }

  if (isSupportAdminOrSchoolAdmin.value) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  if (info.event.classNames.includes('documented')) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  eventsModalsStore.onEditEvent(info.event);
};

const scheduleEvaluationEvent = (studentId?: number, dueAt?: Date) => {
  eventsModalsStore.preSelectedDueAt = dueAt as Date;
  const student = availableAttendees.value.find((attendee) => attendee.value == (studentId as number));
  if (student) eventsModalsStore.setAttendees([student]);
  eventsModalsStore.createNewEvent(EVENT_TYPE.EVALUATION);
};

const filterIsGroupOrIndividualAttendee = () => {
  availableAttendees.value.forEach((attendee) => {
    attendee.disciplines?.forEach((discipline) => {
      if (discipline.is_group) {
        const alreadyInArray = isGroupEligibleAttendees.value.findIndex((eligibleAttendee) => {
          return attendee.value === eligibleAttendee.value;
        });
        if (alreadyInArray === -1) {
          isGroupEligibleAttendees.value.push(attendee);
        }
      }
    });
  });
};

const searchStudent = (event: { query: string }) => {
  autocomplete.value = selectedAttendees.value.length > 0 || event.query.length >= 1;

  if (!event.query.trim()) {
    filteredStudents.value = [...availableAttendees.value];
    return;
  }

  filteredStudents.value = availableAttendees.value.filter((student) => {
    return student.name.toLowerCase().includes(event.query.toLowerCase());
  });
};

const redirecToClassroom = (classroomLink) => {
  window.open(classroomLink, '_blank');
};
</script>

<template>
  <div
    v-if="$tenant.tenantSettingEnabled(TENANT_SETTINGS.UTILIZATION_TRACKING) && canViewUtilizationWidgets"
    class="w-full flex flex-row reporting-widgets"
  >
    <Loader v-if="utilizationStore.loading" class="loader loader-height" />
    <template v-else>
      <SOWReporting class="sow-reporting-widget" :sow-data="utilizationStore.sOWTableData" />
      <TimeRatioReporting
        class="time-ratio-reporting-widget flex-auto"
        :time-ratio-data="utilizationStore.timeRatioTableData"
      />
    </template>
  </div>
  <div v-if="can([PERMISSIONS.OVERAGES_ALL])" class="w-full flex">
    <Overage />
  </div>
  <div
    v-if="can([PERMISSIONS.DASHBOARD_OVERVIEW_CALENDAR_VIEW_SCHEDULE_VIEW])"
    class="dashboard-calendar bg-white border-round w-full mr-4 mb-4 overflow-hidden"
  >
    <div class="dashboard-calendar-row flex w-full h-auto">
      <Calendar v-model="selectedDate" :inline="true" />
      <div class="flex-1 border-100 border-left-2 md:min-width-full h-auto">
        <Loader v-if="calendarStore.loading" class="loader" />
        <div v-if="!calendarStore.loading && !eventsOnSelectedDate" class="p-4 pb-2 z-1 relative">
          <h3 class="font-bold">{{ selectedDate.toLocaleDateString('en-US', { dateStyle: 'medium' }) }}</h3>
          <p class="mt-2">No events</p>
        </div>
        <FullCalendar
          ref="calendar"
          :options="calendarOptions"
          class="flex-1 w-full border-100 border-left-2 md:min-width-full"
          style="min-width: 360px; min-height: 240px"
        >
          <template #eventContent="arg">
            <div class="flex grid w-full text-base md:text-sm lg:text-base">
              <div class="col-6 md:col-5 lg:col-6">
                {{ arg.event.title }}
              </div>
              <div v-if="!isClinician" class="col-5 flex p-1 text-base md:text-sm lg:text-base">
                <Provider
                  :discipline-code="arg.event.extendedProps.discipline_code"
                  :service-provider-name="arg.event.extendedProps.classroom.service_provider_name"
                />
              </div>
              <div
                v-if="canAccessClassroom && arg.event.extendedProps.classroom.classroom_link"
                class="col-1 flex pr-2 align-items-center justify-items-end"
                style="min-width: 16px"
              >
                <a @click.stop="redirecToClassroom(arg.event.extendedProps.classroom.classroom_link)">
                  <span class="material-icons text-primary">videocam</span>
                </a>
              </div>
            </div>
          </template>
        </FullCalendar>
      </div>
    </div>

    <div
      v-if="can([PERMISSIONS.DASHBOARD_OVERVIEW_MEET_WITH_SEARCH_STUDENTS_VIEW]) && availableAttendees.length > 0"
      class="dashboard-calendar-row flex p-3 pb-3 border-100 border-top-2 align-items-center"
    >
      <div class="w-full p-input-icon-left">
        <IconField icon-position="left">
          <InputIcon class="p-input-icon-left" style="z-index: 100">
            <Icon name="search" class="text-lg" />
          </InputIcon>
          <AutoComplete
            v-model="selectedAttendees"
            class="w-full"
            :suggestions="filteredStudents"
            :multiple="true"
            option-label="name"
            :option-disabled="isAlreadySelected"
            placeholder="Create new event with..."
            remove-token-icon="m-icon close text-sm"
            @item-unselect="filterSelected"
            @item-select="filterSelected"
            @complete="searchStudent($event)"
            @clear="autocomplete = false"
          >
            <template #chip="slotProps">
              <div class="flex align-items-center">
                <span class="text-sm">{{ slotProps.value.name }}</span>
                <Icon
                  v-if="slotProps.value.disciplines.some((group) => group.is_group)"
                  name="group"
                  class="icon-group ml-1 text-sm text-color-secondary"
                />
              </div>
            </template>
            <template #option="slotProps">
              <div class="flex align-items-center" :class="{ selected: isAlreadySelected(slotProps.option) }">
                <span class="text-sm">{{ slotProps.option.name }}</span>
                <Icon
                  v-if="slotProps.option.disciplines.some((group) => group.is_group)"
                  name="group"
                  class="icon-group ml-1 text-sm text-color-secondary"
                />
              </div>
            </template>
          </AutoComplete>
        </IconField>
      </div>
      <AppButton
        class="ml-3"
        type="primary"
        :text="
          selectedAttendees.length
            ? 'New Event with ' + selectedAttendees.length + ' attendee' + (selectedAttendees.length > 1 ? 's' : '')
            : 'New Event'
        "
        icon-left="add"
        :disabled="!can([PERMISSIONS.DASHBOARD_OVERVIEW_MEET_WITH_SEARCH_STUDENTS_ADD_EVENT])"
        @click="
          () => eventsModalsStore.setAttendees(selectedAttendees, selectedAttendees.value ?? true).createNewEvent()
        "
      />
    </div>
  </div>
  <DashboardCaseloadAdditions
    v-if="can([PERMISSIONS.REFERRAL_CASELOAD]) && $tenant.tenantSettingEnabled(TENANT_SETTINGS.REFERRALS)"
    :organizations-ids="organizationAssociated"
    @schedule-event="scheduleEvaluationEvent"
  />

  <Attendance v-if="can([PERMISSIONS.WIDGET_ATTENDANCE_ALL])" :organizations-ids="organizationAssociated" />
  <SupportWidget />

  <!-- Disabling Compliance widget for ticket TMS-1997 -->
  <!-- <Compliance v-if="can([PERMISSIONS.WIDGET_COMPLIANCE])" :organizations-ids="organizationAssociated" /> -->
  <Evaluation
    v-if="can([PERMISSIONS.WIDGET_EVALUATION])"
    :organizations-ids="organizationAssociated"
    @schedule-event="scheduleEvaluationEvent"
  />

  <EventsModals />
</template>

<style scoped lang="scss">
@import '@/theme/extensions/calendar';

.loader {
  position: relative;
  top: 50%;
  left: 50%;
  z-index: 50;
  transform: translate(-50%, -50%);
  &.loader-height {
    height: 400px;
  }
}

.sow-reporting-widget {
  flex: 1 1 55% !important;
}

@media (max-width: 1440px) {
  .reporting-widgets {
    flex-wrap: wrap;
    .sow-reporting-widget {
      flex: 1 1 auto !important;
    }
  }
}

.dashboard-calendar {
  width: 100%;

  @media (max-width: 800px) {
    margin-right: 0px !important;
  }
  .dashboard-calendar-row:first-child {
    height: 24rem;
    @media (max-width: 960px) {
      flex-direction: column;
      height: 44rem;
      .fc {
        border-left: none;
        border-top: 2px solid $grayLight;
      }
    }
  }
  :deep(.fc) {
    border-radius: 0px !important;

    .fc-list {
      border-radius: 0px !important;
    }
    .fc-list-day a.fc-list-day-text {
      color: $grayDark;
      &::before {
        content: '';
      }
    }

    .fc-list-event-title {
      min-width: 300px;
      padding: 8px !important;
    }

    .fc-list-empty {
      background: white;
      padding: 1rem;
      color: $grayDark;
    }
  }
}

.p-autocomplete .p-inputtext {
  height: auto;
}
:deep(ul.p-autocomplete-multiple-container) {
  height: auto;
  gap: 5px;
  display: flex;
  flex-wrap: wrap;
  flex: auto;
  padding: 3px 3px 3px 12px;
  flex-direction: initial;
  li.p-autocomplete-token {
    height: auto;
    border-radius: 6px;
    min-width: 112px;
  }
}

.selected {
  opacity: 0.33;
}
</style>
